import React, { useState } from 'react';
import Modal from './Modal';
import api from '../services/api';

// Các trường được gửi khi tạo hồ sơ mới — backend tự đặt is_active = true và tự sinh slug
const PROFILE_FIELDS = {
  title: '',
  headline: '',
  bio: '',
  practicing_since: '',
  public_email: '',
  public_phone: '',
  is_public: false,
  display_order: 1000,
};

const EMPTY_FORM = { ...PROFILE_FIELDS, is_active: true };

function toForm(specialist) {
  if (!specialist) return { ...EMPTY_FORM };
  return {
    title: specialist.title || '',
    headline: specialist.headline || '',
    bio: specialist.bio || '',
    practicing_since: specialist.practicing_since ?? '',
    public_email: specialist.public_email || '',
    public_phone: specialist.public_phone || '',
    is_public: !!specialist.is_public,
    display_order: specialist.display_order ?? 1000,
    is_active: !!specialist.is_active,
  };
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('vi-VN');
}

function SpecialistModal({ user, specialist: initialSpecialist, isSuperAdmin, onClose, onSaved }) {
  const [specialist, setSpecialist] = useState(initialSpecialist || null);
  const [formData, setFormData] = useState(toForm(initialSpecialist));
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const canEdit = isSuperAdmin;
  const isCreating = !specialist;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const next = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    };
    // Thu hồi tư cách thì backend ép is_public = false, phản ánh ngay lên UI cho khỏi hiểu nhầm
    if (name === 'is_active' && !checked) {
      next.is_public = false;
    }
    setFormData(next);
  };

  // practicing_since / display_order là số, ô rỗng phải gửi null thay vì chuỗi rỗng
  const normalize = (field, value) => {
    if (field === 'practicing_since' || field === 'display_order') {
      if (value === '' || value === null) return null;
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed === '' ? null : trimmed;
    }
    return value;
  };

  const buildPayload = () => {
    const payload = {};
    Object.keys(PROFILE_FIELDS).forEach((field) => {
      payload[field] = normalize(field, formData[field]);
    });
    if (payload.display_order === null) payload.display_order = 1000;
    return payload;
  };

  // PATCH chỉ gửi trường thực sự thay đổi, tránh đụng phải các trường bị backend chặn
  const buildPatchPayload = () => {
    const current = toForm(specialist);
    const payload = {};
    Object.keys(EMPTY_FORM).forEach((field) => {
      if (formData[field] !== current[field]) {
        payload[field] = normalize(field, formData[field]);
      }
    });
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      if (isCreating) {
        const created = await api.createSpecialist({
          user_id: user.id,
          slug: null, // để backend tự sinh từ họ tên
          ...buildPayload(),
        });
        setSpecialist(created);
        setFormData(toForm(created));
        onSaved && onSaved();
      } else {
        const payload = buildPatchPayload();
        if (Object.keys(payload).length === 0) {
          onClose();
          return;
        }
        const updated = await api.updateSpecialist(specialist.id, payload);
        setSpecialist(updated);
        setFormData(toForm(updated));
        onSaved && onSaved();
        onClose();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const title = isCreating
    ? `Cấp tư cách chuyên viên — ${user.username}`
    : `Hồ sơ chuyên viên — ${user.username}`;

  // Org admin chỉ được xem
  if (!canEdit) {
    return (
      <Modal title={title} onClose={onClose}>
        {error && <div className="error-message">{error}</div>}

        {!specialist ? (
          <p>
            Người dùng này chưa có hồ sơ chuyên viên. Chỉ super admin mới cấp được tư cách chuyên
            viên — vui lòng liên hệ super admin.
          </p>
        ) : (
          <>
            <div className="specialist-status-row">
              <span
                className={`specialist-badge${specialist.is_active ? '' : ' is-revoked'}`}
              >
                {specialist.is_active ? 'Chuyên viên đang hoạt động' : 'Đã thu hồi tư cách'}
              </span>
              {specialist.is_public && <span className="specialist-badge is-public">Hiện trên website</span>}
            </div>

            <dl className="specialist-view">
              <dt>Chức danh</dt>
              <dd>{specialist.title || '-'}</dd>
              <dt>Giới thiệu ngắn</dt>
              <dd>{specialist.headline || '-'}</dd>
              <dt>Tiểu sử</dt>
              <dd className="specialist-view-bio">{specialist.bio || '-'}</dd>
              <dt>Hành nghề từ năm</dt>
              <dd>
                {specialist.practicing_since || '-'}
                {specialist.years_of_experience != null
                  ? ` (${specialist.years_of_experience} năm kinh nghiệm)`
                  : ''}
              </dd>
              <dt>Email công khai</dt>
              <dd>{specialist.public_email || '-'}</dd>
              <dt>Điện thoại công khai</dt>
              <dd>{specialist.public_phone || '-'}</dd>
              <dt>Tổ chức</dt>
              <dd>{specialist.organization_name || '-'}</dd>
              <dt>Người cấp tư cách</dt>
              <dd>{specialist.granted_by_username || '-'}</dd>
              {specialist.revoked_at && (
                <>
                  <dt>Thu hồi lúc</dt>
                  <dd>{formatDate(specialist.revoked_at)}</dd>
                </>
              )}
            </dl>

            <p className="specialist-view-hint">
              Chỉ super admin mới sửa được hồ sơ chuyên viên.
            </p>
          </>
        )}

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Đóng
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={title} onClose={onClose}>
      {error && <div className="error-message">{error}</div>}

      {!isCreating && (
        <div className="specialist-status-row">
          <span className={`specialist-badge${specialist.is_active ? '' : ' is-revoked'}`}>
            {specialist.is_active ? 'Chuyên viên đang hoạt động' : 'Đã thu hồi tư cách'}
          </span>
          {specialist.revoked_at && (
            <span className="specialist-meta">Thu hồi lúc {formatDate(specialist.revoked_at)}</span>
          )}
          {specialist.granted_by_username && (
            <span className="specialist-meta">Cấp bởi {specialist.granted_by_username}</span>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Chức danh</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="vd. Thạc sĩ Tâm lý lâm sàng"
          />
        </div>

        <div className="form-group">
          <label>Giới thiệu ngắn (1–2 dòng cho card danh sách)</label>
          <input
            type="text"
            name="headline"
            value={formData.headline}
            onChange={handleInputChange}
          />
        </div>

        <div className="form-group">
          <label>Tiểu sử (bằng cấp, lĩnh vực, quá trình công tác)</label>
          <textarea name="bio" rows={6} value={formData.bio} onChange={handleInputChange} />
        </div>

        <div className="form-group">
          <label>Hành nghề từ năm</label>
          <input
            type="number"
            name="practicing_since"
            min={1900}
            max={2100}
            value={formData.practicing_since}
            onChange={handleInputChange}
            placeholder="vd. 2015"
          />
          {!isCreating && specialist.years_of_experience != null && (
            <small className="specialist-meta">
              {specialist.years_of_experience} năm kinh nghiệm (backend tự tính)
            </small>
          )}
        </div>

        <div className="form-group">
          <label>Email công khai</label>
          <input
            type="email"
            name="public_email"
            value={formData.public_email}
            onChange={handleInputChange}
          />
        </div>

        <div className="form-group">
          <label>Điện thoại công khai</label>
          <input
            type="tel"
            name="public_phone"
            value={formData.public_phone}
            onChange={handleInputChange}
          />
        </div>

        <div className="form-group">
          <label>Thứ tự hiển thị (số nhỏ hiện trước)</label>
          <input
            type="number"
            name="display_order"
            value={formData.display_order}
            onChange={handleInputChange}
          />
        </div>

        {!isCreating && (
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
              />
              Còn tư cách chuyên viên (is_active)
            </label>
            <small className="specialist-meta">
              Bỏ tick để thu hồi: người này sẽ không nhận được ca đo mới và mất quyền xem các ca đo
              đã được chia sẻ. Hồ sơ vẫn giữ nguyên, tick lại là khôi phục. Khi thu hồi, backend tự
              tắt "Hiện trên website".
            </small>
          </div>
        )}

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              name="is_public"
              checked={formData.is_public}
              onChange={handleInputChange}
              disabled={!isCreating && !formData.is_active}
            />
            Hiện trên website (is_public)
          </label>
          <small className="specialist-meta">
            Chỉ là hiển thị công khai, không liên quan tới quyền nhận ca đo.
          </small>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {isCreating ? 'Cấp tư cách chuyên viên' : 'Lưu'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Đóng
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default SpecialistModal;
